import { useState } from 'react'
import type { ExternalUnitsState } from '../lib/hooks'
import { externalTypes } from '../lib/mappings'
import type { Integration, MappingResult } from '../lib/types'

interface Props {
  integrations: Integration[]
  external: Map<string, ExternalUnitsState>
  count: number
  pending: boolean
  result: MappingResult | null
  error: string | null
  onApply: (integrationId: string, externalTypeId: string | null) => void
  onClearSelection: () => void
}

/** The fast path: tick a stack of pitches, pick one external type, save them in a single call. */
export default function BulkMapBar({
  integrations,
  external,
  count,
  pending,
  result,
  error,
  onApply,
  onClearSelection,
}: Props) {
  const [integrationId, setIntegrationId] = useState(integrations[0]?.id ?? '')
  const [typeId, setTypeId] = useState('')
  const active = integrations.some((item) => item.id === integrationId) ? integrationId : (integrations[0]?.id ?? '')
  const types = externalTypes(external.get(active)?.units ?? [])
  // Without a fetched list the external ids are still free text, so let them be typed.
  const freeText = types.length === 0

  if (integrations.length === 0) {
    return (
      <div className="bulkbar" role="region" aria-label="Selectie koppelen">
        <strong className="nowrap">{count} geselecteerd</strong>
        <span className="muted small">Geen koppeling die accommodaties kan opsommen, dus niets om aan te koppelen.</span>
        <button type="button" className="btn link" onClick={onClearSelection}>
          Selectie wissen
        </button>
      </div>
    )
  }

  return (
    <div className="bulkbar" role="region" aria-label="Selectie koppelen">
      <strong className="nowrap">{count} geselecteerd</strong>
      {integrations.length > 1 && (
        <label className="check">
          <span className="visually-hidden">Koppeling</span>
          <select value={active} onChange={(event) => setIntegrationId(event.target.value)}>
            {integrations.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="check">
        Koppel selectie aan
        {freeText ? (
          <input
            type="text"
            value={typeId}
            placeholder="type-id uit het boekingssysteem"
            style={{ width: '16rem' }}
            onChange={(event) => setTypeId(event.target.value)}
          />
        ) : (
          <select value={typeId} onChange={(event) => setTypeId(event.target.value)}>
            <option value="">kies een accommodatietype…</option>
            {types.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </select>
        )}
      </label>
      <button
        type="button"
        className="btn primary"
        disabled={pending || typeId.trim() === '' || active === ''}
        onClick={() => onApply(active, typeId.trim())}
      >
        {pending ? 'Opslaan…' : 'Koppelen'}
      </button>
      <button type="button" className="btn danger" disabled={pending || active === ''} onClick={() => onApply(active, null)}>
        Koppeling wissen
      </button>
      <button type="button" className="btn link" onClick={onClearSelection}>
        Selectie wissen
      </button>
      {error && <span className="inline-error">{error}</span>}
      {result && !error && !pending && (
        <span className="inline-ok">
          {result.created} nieuw, {result.updated} bijgewerkt, {result.deleted} verwijderd
          {result.forgottenNights ? `, ${result.forgottenNights} gesynchroniseerde nachten vervallen` : ''}
        </span>
      )}
    </div>
  )
}
